import { FC, useEffect } from 'react';
import { ENV_CONFIG } from 'config/environment';
import { DashboardScreen, trackEvent } from 'helpers/mixpanelEvents';
import { IMPACT_SCORE_DISCLAIMER } from './constants';
import { EmbedBadgeCta } from './EmbedBadgeCta';
import { PublicFundraisingLink } from './PublicFundraisingLink';
import { ResourceLibraryCta } from './ResourceLibraryCta';
import { ShareScoreCta } from './ShareScoreCta';

interface RecipientBadgesCardProps {
  className: string;
  fundraisingUrl: string;
  recipientId: string;
  recipientName: string;
  photoId: string;
  raceDisplayInfo: string | null;
  impactScore: number;
  refCode: string;
  badgeUrl: string;
  badgeYear: string;
}

// Appended to the fundraising link as its own `&ref=` param so a donation can be traced back
// to the CTA that produced it. Must not be concatenated directly onto fundraisingUrl — the
// `p` value it ends in is a page lookup key, so mutating it 404s the donate page.
const SHARE_REF_CODE = 'share';
const EMBED_REF_CODE = 'badge';

const SCREEN: DashboardScreen = 'recipients_dashboard';

export const RecipientBadgesCard: FC<RecipientBadgesCardProps> = ({
  className,
  fundraisingUrl,
  recipientId,
  recipientName,
  photoId,
  raceDisplayInfo,
  impactScore,
  refCode,
  badgeUrl,
  badgeYear,
}) => {
  const photoUrl = `${ENV_CONFIG().CLOUD_STORAGE.URL}/recipients/rec_${photoId}.jpg`;

  useEffect(() => {
    trackEvent('Recipient Badge Section Viewed', {
      screen: SCREEN,
      impact_score: impactScore,
      recipient_id: recipientId,
    });
  }, [recipientId, impactScore]);

  return (
    <div className={className}>
      <div className="card recipient-badges-card">
        <div className="recipient-badges-card__hero">
          <img src={photoUrl} alt={recipientName} className="recipient-badges-card__photo" />
          <div className="recipient-badges-card__identity">
            <span className="h4-text text-white">{recipientName}</span>
            {raceDisplayInfo && (
              <span className="body-small-text text-white">{raceDisplayInfo}</span>
            )}
          </div>
          <div className="recipient-badges-card__score">
            <span className="eyebrow-text text-white">Impact Score</span>
            <div className="recipient-badges-card__score-value">
              <span className="h1-dtm text-white">{impactScore.toFixed(1)}</span>
              <span className="body-small-text text-white">/10</span>
            </div>
          </div>
        </div>

        <div className="card-body recipients-card-content">
          <PublicFundraisingLink
            className="recipient-badges-card__link"
            url={fundraisingUrl}
            hideCardWrapper
            invertCopyButton
          />

          <div className="recipient-badges-card__ctas">
            <EmbedBadgeCta
              impactScore={impactScore}
              fundraisingUrl={fundraisingUrl}
              badgeUrl={badgeUrl}
              refParam={`&ref=${refCode}-${EMBED_REF_CODE}`}
            />
            <ShareScoreCta
              impactScore={impactScore}
              fundraisingUrl={fundraisingUrl}
              badgeYear={badgeYear}
              refParam={`&ref=${refCode}-${SHARE_REF_CODE}`}
            />
            <ResourceLibraryCta />
          </div>

          <span className="body-small-text color-gray recipient-badges-card__disclaimer">
            {IMPACT_SCORE_DISCLAIMER}
          </span>
        </div>
      </div>
    </div>
  );
};
